import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../utils/constants';
import { FaStar, FaFilter, FaFlag, FaUser, FaTimes, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ReviewAndRatingScreen.css';

const ReviewAndRatingScreen = () => {
  const [reviews, setReviews] = useState([]);
  const [filterRating, setFilterRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      
      if (!accessToken) {
        throw new Error('Access token not found. Please log in again.');
      }

      const response = await fetch(`${API_BASE_URL}api/doctor/reviews`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      } else {
        console.error('Failed to fetch reviews:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to fetch reviews.');
    } finally {
      setLoading(false);
    }
  };

  const overallRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  const filteredReviews = filterRating
    ? reviews.filter((review) => review.rating === filterRating)
    : reviews;

  const reportReasons = [
    'Inappropriate content',
    'Spam or fake review',
    'Offensive language',
    'False information',
    'Other'
  ];

  const openReportModal = (review) => {
    setSelectedReview(review);
    setReportModalVisible(true);
    setReportReason('');
    setCustomReason('');
  };

  const submitReport = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting.');
      return;
    }

    if (reportReason === 'Other' && !customReason.trim()) {
      toast.error('Please provide a custom reason.');
      return;
    }

    setReportLoading(true);
    try {
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      if (!accessToken) {
        throw new Error('Access token not found. Please log in again.');
      }

      const response = await fetch(`${API_BASE_URL}api/doctor/report-review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reviewId: selectedReview._id,
          reason: reportReason === 'Other' ? customReason : reportReason,
        }),
      });

      if (response.ok) {
        toast.success('The review has been reported successfully.');
        setReportModalVisible(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to report the review.');
      }
    } catch (error) {
      console.error('Error reporting review:', error);
      toast.error(error.message || 'Failed to report the review.');
    } finally {
      setReportLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FaStar
        key={index}
        className={`star ${index < rating ? 'filled' : 'empty'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="loading-spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <DashboardLayout title="Reviews & Ratings" showBackButton={true}>
      <div className="reviews-container">
        <div className="reviews-header">
          <h1>Your Reviews and Ratings</h1>
          <p>See what your patients are saying about you</p>
        </div>

        {/* Overall Rating Card */}
        <div className="overall-rating-card">
          <div className="rating-icon">
            <FaStar />
          </div>
          <div className="rating-content">
            <h2 className="overall-rating">{overallRating}</h2>
            <p className="rating-label">Overall Rating</p>
            <p className="rating-subtitle">Based on {reviews.length} reviews</p>
          </div>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-header">
            <FaFilter className="filter-icon" />
            <span className="filter-label">Filter by Rating:</span>
          </div>
          <div className="filter-buttons">
            <button
              className={`filter-button ${filterRating === null ? 'active' : ''}`}
              onClick={() => setFilterRating(null)}
            >
              All
            </button>
            {[5, 4, 3, 2, 1].map((rating) => (
              <button
                key={rating}
                className={`filter-button ${filterRating === rating ? 'active' : ''}`}
                onClick={() => setFilterRating(filterRating === rating ? null : rating)}
              >
                {rating}★
              </button>
            ))}
          </div>
        </div>

        {/* Reviews List */}
        <div className="reviews-list">
          {filteredReviews.length === 0 ? (
            <div className="no-reviews">
              <FaUser className="no-reviews-icon" />
              <h3>No reviews available</h3>
              <p>You haven't received any reviews yet. Keep providing excellent service!</p>
            </div>
          ) : (
            filteredReviews.map((review) => (
              <div key={review._id || review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-avatar">
                      {review?.patientId?.fullName?.charAt(0) || 'P'}
                    </div>
                    <div className="reviewer-details">
                      <h4 className="reviewer-name">
                        {review?.patientId?.fullName || 'Anonymous Patient'}
                      </h4>
                      <div className="review-rating">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                  </div>
                  <div className="review-date">
                    {new Date(review.date).toLocaleDateString()}
                  </div>
                </div>

                <div className="review-content">
                  <p className="review-text">{review.review}</p>
                </div>

                <div className="review-actions">
                  {review.isDisputed ? (
                    <div className="disputed-container">
                      <FaInfoCircle className="disputed-icon" />
                      <span className="disputed-text">
                        We're looking into this. You have already submitted the dispute.
                      </span>
                    </div>
                  ) : (
                    <button
                      className="report-button"
                      onClick={() => openReportModal(review)}
                    >
                      <FaFlag />
                      Report
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        {/* Report Modal */}
        {reportModalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Report Review</h3>
                <button className="close-button" onClick={() => setReportModalVisible(false)}>
                  <FaTimes />
                </button>
              </div>
              
              <p className="modal-subtitle">Why are you reporting this review?</p>
              
              <div className="reason-options">
                {reportReasons.map((reason) => (
                  <div
                    key={reason}
                    className={`reason-option ${reportReason === reason ? 'selected' : ''}`}
                    onClick={() => setReportReason(reason)}
                  >
                    <div className="radio-button">
                      {reportReason === reason && <div className="radio-selected" />}
                    </div>
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
              
              {reportReason === 'Other' && (
                <textarea
                  className="custom-reason-input"
                  placeholder="Please specify your reason..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows="3"
                />
              )}
              
              <div className="modal-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => setReportModalVisible(false)}
                >
                  Cancel
                </button>
                <button 
                  className="submit-button" 
                  onClick={submitReport}
                  disabled={reportLoading}
                >
                  {reportLoading ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReviewAndRatingScreen;